import { useEffect, useMemo, useState } from 'react';
import { i18n } from '@wms/shared';
import { api } from '../lib/api.js';
import { formatQty } from '../lib/format.js';
import { Dialog } from './Dialog.js';

const { t } = i18n;

interface PalletSummary {
  id: string;
  code: string;
  state: string;
  contentsVisibleAt: string | null;
  depletedAt: string | null;
  ownerPartner: { id: string; code: string; name: string } | null;
}

interface PalletDetail extends PalletSummary {
  contents: {
    skuId: string;
    remainingQty: string;
    sku: { code: string; nameLt: string };
  }[];
}

export function ConsolidatePalletDialog({
  onClose,
  onConsolidated,
}: {
  onClose: () => void;
  onConsolidated: (palletId: string) => void;
}) {
  const [pallets, setPallets] = useState<PalletSummary[] | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [details, setDetails] = useState<PalletDetail[]>([]);
  const [confirmed, setConfirmed] = useState(false);
  const [reason, setReason] = useState('Palečių sujungimas');
  const [locationRow, setLocationRow] = useState('');
  const [locationShelf, setLocationShelf] = useState('');
  const [locationPosition, setLocationPosition] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api<PalletSummary[]>('/pallets')
      .then((rows) =>
        setPallets(
          rows.filter(
            (row) =>
              row.ownerPartner &&
              row.contentsVisibleAt &&
              !row.depletedAt &&
              ['ARRIVED', 'PARTIALLY_DISPATCHED', 'DAMAGED'].includes(row.state),
          ),
        ),
      )
      .catch(() => setError(t('common.error')));
  }, []);

  useEffect(() => {
    const ids = [...selected];
    if (ids.length === 0) {
      setDetails([]);
      return;
    }
    Promise.all(ids.map((id) => api<PalletDetail>(`/pallets/${id}`)))
      .then(setDetails)
      .catch(() => setError(t('common.error')));
    setConfirmed(false);
  }, [selected]);

  const selectedPartnerId = details[0]?.ownerPartner?.id ?? null;
  const options = useMemo(
    () =>
      (pallets ?? []).filter(
        (pallet) => !selectedPartnerId || pallet.ownerPartner?.id === selectedPartnerId,
      ),
    [pallets, selectedPartnerId],
  );

  async function consolidate() {
    if (selected.size < 2 || !confirmed || !reason.trim()) return;
    const lines = details.flatMap((pallet) =>
      pallet.contents
        .filter((line) => Number(line.remainingQty) > 0)
        .map((line) => ({
          sourcePalletId: pallet.id,
          skuId: line.skuId,
          qty: Number(line.remainingQty),
        })),
    );
    setBusy(true);
    setError(null);
    try {
      const result = await api<{ pallet: { id: string } }>('/pallets/consolidate', {
        method: 'POST',
        body: {
          sourcePalletIds: [...selected],
          lines,
          reason: reason.trim(),
          locationRow: locationRow.trim() || undefined,
          locationShelf: locationShelf.trim() || undefined,
          locationPosition: locationPosition.trim() || undefined,
        },
      });
      onConsolidated(result.pallet.id);
    } catch {
      setError(t('common.error'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog title={t('pallet.consolidate.title')} onClose={onClose} size="wide" height="tall">
      <div className="space-y-4 text-sm">
        <p className="text-xs text-muted-foreground">{t('pallet.consolidate.hint')}</p>
        <div className="grid gap-2 sm:grid-cols-2">
          {options.map((pallet) => (
            <label
              key={pallet.id}
              className="flex cursor-pointer items-center gap-2 rounded border border-border p-2"
            >
              <input
                type="checkbox"
                checked={selected.has(pallet.id)}
                onChange={() =>
                  setSelected((current) => {
                    const next = new Set(current);
                    if (next.has(pallet.id)) next.delete(pallet.id);
                    else next.add(pallet.id);
                    return next;
                  })
                }
              />
              <span>
                <span className="font-mono">{pallet.code}</span>
                <span className="ml-2 text-xs text-muted-foreground">
                  {pallet.ownerPartner?.code}
                </span>
              </span>
            </label>
          ))}
        </div>

        {details.length > 0 ? (
          <div className="space-y-2 rounded border border-border p-3">
            <div className="font-medium">{t('pallet.consolidate.confirm_contents')}</div>
            {details.map((pallet) => (
              <div key={pallet.id} className="text-xs">
                <span className="font-mono font-medium">{pallet.code}</span>{' '}
                <span className="text-muted-foreground">
                  {pallet.contents
                    .filter((line) => Number(line.remainingQty) > 0)
                    .map((line) => `${line.sku.code} × ${formatQty(line.remainingQty)}`)
                    .join(' · ')}
                </span>
              </div>
            ))}
            <label className="flex items-start gap-2 pt-1">
              <input
                type="checkbox"
                checked={confirmed}
                onChange={(event) => setConfirmed(event.target.checked)}
                className="mt-0.5"
              />
              <span>{t('pallet.consolidate.confirm_checkbox')}</span>
            </label>
          </div>
        ) : null}

        <label className="block space-y-1">
          <span className="font-medium">{t('pallet.transition.reason_label')}</span>
          <input
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            className="w-full rounded border border-border bg-background px-2 py-1.5"
          />
        </label>
        <div className="grid grid-cols-3 gap-2">
          <input
            value={locationRow}
            onChange={(event) => setLocationRow(event.target.value)}
            maxLength={8}
            placeholder={t('location.row')}
            className="rounded border border-border bg-background px-2 py-1.5 text-center font-mono"
          />
          <input
            value={locationShelf}
            onChange={(event) => setLocationShelf(event.target.value)}
            maxLength={8}
            placeholder={t('location.shelf')}
            className="rounded border border-border bg-background px-2 py-1.5 text-center font-mono"
          />
          <input
            value={locationPosition}
            onChange={(event) => setLocationPosition(event.target.value)}
            maxLength={8}
            placeholder={t('location.position')}
            className="rounded border border-border bg-background px-2 py-1.5 text-center font-mono"
          />
        </div>
        {error ? <div className="text-red-600">{error}</div> : null}
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded border border-border px-3 py-1.5"
          >
            {t('common.cancel')}
          </button>
          <button
            type="button"
            onClick={() => void consolidate()}
            disabled={busy || selected.size < 2 || !confirmed || !reason.trim()}
            className="rounded bg-primary px-3 py-1.5 text-primary-foreground disabled:opacity-50"
          >
            {busy ? t('common.loading') : t('pallet.consolidate.submit')}
          </button>
        </div>
      </div>
    </Dialog>
  );
}
